import { createDraft, finishDraft, produce } from 'immer';
import ContainerNode from './ContainerNode';
import ModuleNode from './ModuleNode';
import RootNode from './RootNode';
import SectionNode from './SectionNode';
import { GRID_NODE_TYPES, GRID_NODE_TYPE_TO_DOM_UNIQUE_ID } from './constants';
import { concatenateId, isRootNode, isSectionNode, isContainerNode, isModuleNode, findNodeInTree, iterateFrom, getGridLayoutIterator, generateUniqueId, invertObjectKeysAndValues, getGridContainerIterator, convertMapOfJsonToMapOfAvro } from './utils';
import { exportForGridLayoutDataApi, importContainerJsonToContainerNodeValue, importGridLayoutJsonToRootNodeValue, importModuleJsonToModuleNodeValue, importSectionJsonToSectionNodeValue } from './serialize';
import { isContainer, isModule, isSection } from './typenameUtils';
const noop = value => value;
export default class BaseGridLayoutDataTree {
  constructor(params) {
    const {
      RootNodeClass = RootNode,
      SectionNodeClass = SectionNode,
      ContainerNodeClass = ContainerNode,
      ModuleNodeClass = ModuleNode,
      rootId,
      rootNodeValue,
      initialState,
      generatedIdQueueWriter,
      generatedIdQueueReader
    } = params;
    this.RootNodeClass = RootNodeClass;
    this.SectionNodeClass = SectionNodeClass;
    this.ContainerNodeClass = ContainerNodeClass;
    this.ModuleNodeClass = ModuleNodeClass;
    this._isDraftInProgress = false;
    this.generatedIdQueueWriter = generatedIdQueueWriter;
    this.generatedIdQueueReader = generatedIdQueueReader;
    if (initialState) {
      this._state = initialState;
    } else {
      const newRootId = rootId || concatenateId(GRID_NODE_TYPES.ROOT, 0);
      const rootNode = this.createNewRootNode({
        id: newRootId,
        value: rootNodeValue,
        type: GRID_NODE_TYPES.ROOT,
        childNodeIds: [],
        treeRef: this
      });
      this._state = {
        idCounter: 1,
        rootNode,
        sectionsById: {},
        containersById: {},
        modulesById: {}
      };
    }
  }
  static createTreeFromGridLayoutDataApi(gridLayout, {
    transformNodeValueCallback = noop,
    RootNodeClass,
    SectionNodeClass,
    ContainerNodeClass,
    ModuleNodeClass,
    generatedIdQueueWriter,
    generatedIdQueueReader
  } = {}) {
    // Create the root node value from the grid layout and create the tree
    const rootNodeValue = transformNodeValueCallback(importGridLayoutJsonToRootNodeValue(gridLayout));
    const tree = new this({
      rootId: gridLayout.name,
      rootNodeValue,
      RootNodeClass,
      SectionNodeClass,
      ContainerNodeClass,
      ModuleNodeClass,
      generatedIdQueueWriter,
      generatedIdQueueReader
    });

    // Use batch update for efficient tree construction
    return tree.withBatchUpdate(() => {
      const rootNode = tree.getRootNode();

      // Iterate over the grid layout and insert the nodes into the tree
      const sectionCallback = ({
        section
      }) => {
        const newSectionNodeValue = transformNodeValueCallback(importSectionJsonToSectionNodeValue(section));
        const {
          node,
          tree: newTree
        } = tree.insertNewNode({
          newNodeValue: newSectionNodeValue,
          parentNodeId: rootNode.getId()
        });
        return {
          node: newTree.findNode(node.getId()),
          tree: newTree
        };
      };
      const gridContentCallback = ({
        gridContent,
        parentNodeId
      }) => {
        if (isContainer(gridContent)) {
          const newContainerNodeValue = transformNodeValueCallback(importContainerJsonToContainerNodeValue(gridContent));
          const {
            node,
            tree: newTree
          } = tree.insertNewNode({
            newNodeValue: newContainerNodeValue,
            parentNodeId
          });
          return {
            node: newTree.findNode(node.getId()),
            tree: newTree
          };
        } else {
          const newModuleNodeValue = transformNodeValueCallback(importModuleJsonToModuleNodeValue(gridContent));
          const {
            node,
            tree: newTree
          } = tree.insertNewNode({
            newNodeValue: newModuleNodeValue,
            parentNodeId
          });
          return {
            node: newTree.findNode(node.getId()),
            tree: newTree
          };
        }
      };
      const iterator = getGridLayoutIterator(gridLayout);
      iterator({
        sectionCallback,
        gridContentCallback
      });
      return tree;
    });
  }

  /**
   * Serializes the tree to a GridLayout object.
   *
   * @param options - Serialization options
   * @param options.removeEmptySections - Whether to exclude sections with no children (default: false)
   * @param options.isJacksonSerialize - Use jackson serialization for editor 1.0 compatibility (default: false)
   * @returns GridLayout object representing the serialized tree
   */
  serializeTree({
    removeEmptySections = false,
    isJacksonSerialize = false
  } = {}) {
    if (isJacksonSerialize) {
      return exportForGridLayoutDataApi(this);
    }
    return this.getRootNode().serializeNode({
      removeEmptySections
    });
  }

  /**
   * Implements "implicit wrapping" on the specified section with the provided new container node. The `newSectionContainerNodeValue` will
   * become the container child of the new section, with the old section container as its child.
   *
   * @param param0.sectionId - The id of the section to wrap.
   * @param param0.newSectionContainerNodeValue - The value of the new section container node.
   */
  wrapSectionContainer({
    sectionId,
    newSectionContainerNodeValue
  }) {
    const sectionNode = this.findSection(sectionId);
    const oldSectionContainerNode = sectionNode.getChildContainer();
    if (!sectionNode) {
      throw new Error(`Section node with id ${sectionId} not found`);
    }
    let newTree;

    // Create new section, with no children to start.
    const newSectionId = this.generateUniqueNodeId(GRID_NODE_TYPE_TO_DOM_UNIQUE_ID[sectionNode.getType()]);
    const newSectionNode = sectionNode.cloneAndChange({
      childNodeIds: [],
      id: newSectionId
    });
    ({
      tree: newTree
    } = this.insertNewNode({
      newNodeValue: newSectionNode.getValue(),
      parentNodeId: this.getRootId(),
      afterNodeId: sectionNode.getId()
    }));

    // Insert the new section container node.
    ({
      tree: newTree
    } = newTree.insertNewNode({
      newNodeValue: newSectionContainerNodeValue,
      parentNodeId: newSectionId
    }));

    // Implict wrapping step: Move the old section container node to the new section container node.
    ({
      tree: newTree
    } = newTree.moveNode({
      nodeId: oldSectionContainerNode.getId(),
      destParentNodeId: newSectionContainerNodeValue.id
    }));

    // Directly delete the reference to the old section node, the old children are deleted by the moveNode call above.
    newTree.setState(draft => {
      delete draft.sectionsById[sectionId];
    });

    // Set the tree refs for all nodes in the tree.
    newTree.setNodeTreeRefs(newTree);
    return {
      node: newTree.findNode(newSectionId),
      tree: newTree
    };
  }
  insertSubtree({
    subtree,
    parentNodeId,
    beforeNodeId,
    afterNodeId
  }) {
    if (!isSection(subtree) && isModule(subtree)) {
      const {
        tree
      } = this.insertNewNode({
        newNodeValue: importModuleJsonToModuleNodeValue(subtree),
        parentNodeId,
        beforeNodeId,
        afterNodeId
      });
      return tree;
    }
    const tree = this.maybeCloneTreeAndSetTreeRefs();
    return tree.withBatchUpdate(() => {
      const rootNode = tree.getRootNode();

      // Iterate over the grid layout and insert the nodes into the tree
      const sectionCallback = ({
        section
      }) => {
        const newSectionNodeValue = importSectionJsonToSectionNodeValue(section);
        const {
          node,
          tree: newTree
        } = tree.insertNewNode({
          newNodeValue: newSectionNodeValue,
          parentNodeId: rootNode.getId(),
          beforeNodeId,
          afterNodeId
        });
        return {
          node: newTree.findNode(node.getId()),
          tree: newTree
        };
      };
      const gridContentCallback = ({
        gridContent,
        parentNodeId: containerParentNodeId
      }) => {
        if (isContainer(gridContent)) {
          const newContainerNodeValue = importContainerJsonToContainerNodeValue(gridContent);
          const {
            node,
            tree: newTree
          } = tree.insertNewNode({
            newNodeValue: newContainerNodeValue,
            parentNodeId: containerParentNodeId,
            beforeNodeId,
            afterNodeId
          });
          return {
            node: newTree.findNode(node.getId()),
            tree: newTree
          };
        } else {
          const newModuleNodeValue = importModuleJsonToModuleNodeValue(gridContent);
          const {
            node,
            tree: newTree
          } = tree.insertNewNode({
            newNodeValue: newModuleNodeValue,
            parentNodeId: containerParentNodeId,
            beforeNodeId,
            afterNodeId
          });
          return {
            node: newTree.findNode(node.getId()),
            tree: newTree
          };
        }
      };
      const iterator = getGridContainerIterator({
        subtree,
        parentNodeId,
        beforeNodeId,
        afterNodeId
      });
      iterator({
        sectionCallback,
        gridContentCallback
      });
      return tree;
    });
  }
  createNewNode(gridNodeParams) {
    if (gridNodeParams.type === GRID_NODE_TYPES.ROOT) {
      return this.createNewRootNode(gridNodeParams);
    } else if (gridNodeParams.type === GRID_NODE_TYPES.SECTION) {
      return this.createNewSection(gridNodeParams);
    } else if (gridNodeParams.type === GRID_NODE_TYPES.CONTAINER) {
      return this.createNewContainer(gridNodeParams);
    } else if (gridNodeParams.type === GRID_NODE_TYPES.MODULE) {
      return this.createNewModule(gridNodeParams);
    }
    throw new Error(`Invalid node type: ${gridNodeParams.type}`);
  }
  createNewRootNode(rootNodeParams) {
    return new this.RootNodeClass(rootNodeParams);
  }
  createNewModule(moduleNodeParams) {
    return new this.ModuleNodeClass(moduleNodeParams);
  }
  createNewContainer(containerNodeParams) {
    return new this.ContainerNodeClass(containerNodeParams);
  }
  createNewSection(sectionNodeParams) {
    return new this.SectionNodeClass(sectionNodeParams);
  }
  insertNode({
    nodeToInsert,
    destParentNodeId,
    destNodeIndex: _destNodeIndex,
    targetTree = this
  }) {
    let destNodeIndex = _destNodeIndex;
    const newNodeToValidate = nodeToInsert.cloneAndChange({
      parentNodeId: destParentNodeId,
      treeRef: targetTree
    });
    const parentNodeToValidate = targetTree.findNode(destParentNodeId);
    const {
      node: newNode,
      parentNode
    } = targetTree.validateNodePlacement(newNodeToValidate, parentNodeToValidate);

    // If the destNodeIndex is -1, then we can fallback to appending to the end
    // of the parent node
    if (destNodeIndex === -1) {
      destNodeIndex = parentNode.getChildNodeIds().length;
    }

    // Insert the new node id into the target index in the parent child node ids
    const newParentChildNodeIds = [...parentNode.getChildNodeIds()];
    newParentChildNodeIds.splice(destNodeIndex, 0, newNode.getId());
    const newParentNode = parentNode.cloneAndChange({
      childNodeIds: newParentChildNodeIds
    });
    targetTree.setState(draft => {
      // First, update the parent node in the state with the new node we just
      // created
      if (isRootNode(newParentNode)) {
        draft.rootNode = newParentNode;
      } else if (isSectionNode(newParentNode)) {
        draft.sectionsById[newParentNode.getId()] = newParentNode;
      } else if (isContainerNode(newParentNode)) {
        draft.containersById[newParentNode.getId()] = newParentNode;
      }

      // Then, add the new node itself to the state
      if (isSectionNode(newNode)) {
        draft.sectionsById[newNode.getId()] = newNode;
      } else if (isContainerNode(newNode)) {
        draft.containersById[newNode.getId()] = newNode;
      } else if (isModuleNode(newNode)) {
        draft.modulesById[newNode.getId()] = newNode;
      }
    });
    if (isSectionNode(newNode)) {
      return {
        node: newNode,
        tree: targetTree
      };
    } else if (isContainerNode(newNode)) {
      return {
        node: newNode,
        tree: targetTree
      };
    } else {
      return {
        node: newNode,
        tree: targetTree
      };
    }
  }
  insertNewNode({
    newNodeValue,
    parentNodeId,
    beforeNodeId,
    afterNodeId
  }) {
    var _newNodeValue$id, _newNodeValue$id2;
    if (typeof newNodeValue.type !== 'string') {
      throw new Error('Invalid node type');
    }
    const destNodeIndex = this.getDestNodeIndex({
      parentNodeId,
      beforeNodeId,
      afterNodeId
    });
    const nodeId = (_newNodeValue$id = newNodeValue.id) !== null && _newNodeValue$id !== void 0 ? _newNodeValue$id : this.generateUniqueNodeId(newNodeValue.type);
    const newNode = this.createNewNode({
      id: (_newNodeValue$id2 = newNodeValue.id) !== null && _newNodeValue$id2 !== void 0 ? _newNodeValue$id2 : this.generateUniqueNodeId(newNodeValue.type),
      type: newNodeValue.type,
      value: newNodeValue,
      treeRef: this
    });
    const targetTree = this.maybeCloneTreeAndSetTreeRefs();
    const {
      tree: newTree
    } = this.insertNode({
      nodeToInsert: newNode,
      destParentNodeId: parentNodeId,
      destNodeIndex,
      targetTree
    });
    return {
      node: newTree.findNode(nodeId),
      tree: newTree
    };
  }
  removeNode(nodeId) {
    let treeToRemoveNodeFrom = this.maybeCloneTreeAndSetTreeRefs();

    // Find the node to remove
    const nodeToRemove = treeToRemoveNodeFrom.findNode(nodeId);
    if (!nodeToRemove.hasParentNode()) {
      throw new Error(`Cannot remove node ${nodeToRemove.getId()} since it has no parent; this is likely the root node`);
    }
    const parentNode = nodeToRemove.getParentNode();
    const originalIndex = parentNode.indexOfChildNode(nodeId);
    if (originalIndex === -1) {
      throw new Error(`Node ${nodeId} not found in parent's children`);
    }
    const newParentChildNodeIds = [...parentNode.getChildNodeIds()];
    newParentChildNodeIds.splice(originalIndex, 1);
    let newParentNode = parentNode.cloneAndChange({
      childNodeIds: newParentChildNodeIds
    });
    const deletedChildren = [];
    treeToRemoveNodeFrom.setState(draft => {
      // Update the parent node in the state with the new parent node which now
      // has the target node removed from its child node ids
      if (isRootNode(newParentNode)) {
        draft.rootNode = newParentNode;
      } else if (isSectionNode(newParentNode)) {
        draft.sectionsById[newParentNode.getId()] = newParentNode;
      } else if (isContainerNode(newParentNode)) {
        draft.containersById[newParentNode.getId()] = newParentNode;
      }

      // Then delete all child nodes of the removed node from our nodesById maps
      iterateFrom(nodeToRemove, {
        gridNodeCallback: ({
          gridNode
        }) => {
          if (gridNode.getId() !== nodeId) {
            deletedChildren.push(gridNode);
          }
          if (isSectionNode(gridNode)) {
            delete draft.sectionsById[gridNode.getId()];
          } else if (isContainerNode(gridNode)) {
            delete draft.containersById[gridNode.getId()];
          } else if (isModuleNode(gridNode)) {
            delete draft.modulesById[gridNode.getId()];
          }
        }
      });
    });
    if (newParentNode.isSection() && newParentNode.isLeaf()) {
      const {
        tree: newTree,
        removedNode
      } = treeToRemoveNodeFrom.removeNode(newParentNode.getId());
      treeToRemoveNodeFrom = newTree;
      newParentNode = removedNode;
    }
    return {
      removedNode: nodeToRemove,
      removedNodeParentNode: newParentNode,
      originalIndex,
      deletedChildren,
      tree: treeToRemoveNodeFrom
    };
  }
  moveNode({
    nodeId,
    originTree,
    destParentNodeId,
    beforeNodeId,
    afterNodeId
  }) {
    if (beforeNodeId || afterNodeId) {
      const targetSiblingNodeId = beforeNodeId || afterNodeId;
      if (targetSiblingNodeId === nodeId) {
        // No-op since the node is already in the correct position
        return {
          node: this.findNode(nodeId),
          tree: this
        };
      }
    }
    const treeToRemoveNodeFrom = originTree !== null && originTree !== void 0 ? originTree : this;
    const {
      removedNode: nodeToMove,
      tree: treeRemovedFrom,
      deletedChildren: movedChildren
    } = treeToRemoveNodeFrom.removeNode(nodeId);

    // If originTree is provided, then the intent is to insert the node into
    // the current tree context, since we just removed the node from the origin
    // tree. Otherwise we should be using the new tree reference from removeNode
    // since we're just moving it in the same tree
    const treeToInsertWith = originTree ? this.maybeCloneTreeAndSetTreeRefs() : treeRemovedFrom;
    const destNodeIndex = treeToInsertWith.getDestNodeIndex({
      parentNodeId: destParentNodeId,
      beforeNodeId,
      afterNodeId
    });
    const {
      node: newNode,
      tree: newTree
    } = treeToInsertWith.insertNode({
      nodeToInsert: nodeToMove,
      destParentNodeId,
      destNodeIndex
    });

    // Transfer the children of the moved node to the new tree
    newTree.setState(draft => {
      for (const childNode of movedChildren) {
        if (isSectionNode(childNode)) {
          draft.sectionsById[childNode.getId()] = childNode;
        }
        if (isModuleNode(childNode)) {
          draft.modulesById[childNode.getId()] = childNode;
        }
        if (isContainerNode(childNode)) {
          draft.containersById[childNode.getId()] = childNode;
        }
      }
    });
    return {
      node: newTree.findNode(newNode.getId()),
      tree: newTree,
      newOriginTree: treeRemovedFrom
    };
  }
  cloneNode({
    nodeId
  }) {
    const nodeToClone = this.findNode(nodeId);
    if (isRootNode(nodeToClone)) {
      throw new Error('Cannot clone root node');
    }
    const originalIdToClonedIdMap = {};
    const clonedNodeId = this.generateUniqueNodeId(GRID_NODE_TYPE_TO_DOM_UNIQUE_ID[nodeToClone.getType()]);
    const nodeToCloneWithoutChildren = this.cloneNodeHelper(nodeToClone, clonedNodeId);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.insertNode({
      nodeToInsert: nodeToCloneWithoutChildren,
      destParentNodeId: nodeToClone.getParentNodeId(),
      // Place the cloned node right after the original node in the parent node
      destNodeIndex: newTree.getDestNodeIndex({
        parentNodeId: nodeToClone.getParentNodeId(),
        afterNodeId: nodeToClone.getId()
      })
    });
    originalIdToClonedIdMap[nodeToClone.getId()] = clonedNodeId;
    iterateFrom(nodeToClone, {
      // Don't include the start node in the iteration since we already
      // inserted it above. This also means that we don't expect to iterate
      // through a section node or root node.
      includeStartNode: false,
      gridNodeCallback: ({
        gridNode,
        parentNode
      }) => {
        if (gridNode.isRoot()) {
          return;
        }
        if (!parentNode) {
          throw new Error(`Unexpected parent node not found while cloning node ${gridNode.getId()}`);
        }
        const newNodeId = newTree.generateUniqueNodeId(GRID_NODE_TYPE_TO_DOM_UNIQUE_ID[gridNode.getType()]);
        if (!originalIdToClonedIdMap[gridNode.getId()]) {
          originalIdToClonedIdMap[gridNode.getId()] = newNodeId;
        }
        if (isContainerNode(gridNode)) {
          const clonedContainerNode = newTree.cloneNodeHelper(gridNode, newNodeId);
          return newTree.insertNode({
            nodeToInsert: clonedContainerNode,
            destParentNodeId: originalIdToClonedIdMap[parentNode.getId()],
            destNodeIndex: gridNode.getParentNode().indexOfChildNode(gridNode.getId())
          });
        } else if (isModuleNode(gridNode)) {
          const clonedModuleNode = newTree.cloneNodeHelper(gridNode, newNodeId);
          return newTree.insertNode({
            nodeToInsert: clonedModuleNode,
            destParentNodeId: originalIdToClonedIdMap[parentNode.getId()],
            destNodeIndex: gridNode.getParentNode().indexOfChildNode(gridNode.getId())
          });
        } else {
          throw new Error(`Unexpected node type ${gridNode.getType()} encountered while cloning`);
        }
      }
    });
    newTree.setNodeTreeRefs(newTree);

    // Return the latest version of the cloned node since our old reference
    // is now stale from cloning and inserting all of its descendants.
    return {
      node: newTree.findNode(clonedNodeId),
      tree: newTree,
      clonedIdToOriginalIdMap: invertObjectKeysAndValues(originalIdToClonedIdMap)
    };
  }
  mergeNodeValue(nodeId, partialValue, path) {
    const node = this.findNode(nodeId);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.setState(draft => {
      if (isRootNode(node)) {
        draft.rootNode = node.mergeIntoValue(partialValue, path);
      } else if (isSectionNode(node)) {
        draft.sectionsById[node.getId()] = node.mergeIntoValue(partialValue, path);
      } else if (isContainerNode(node)) {
        draft.containersById[node.getId()] = node.mergeIntoValue(partialValue, path);
      } else if (isModuleNode(node)) {
        draft.modulesById[node.getId()] = node.mergeIntoValue(partialValue, path);
      }
    });
    return {
      node: newTree.findNode(nodeId),
      tree: newTree
    };
  }
  mergeSectionValue(id, value) {
    const node = this.findSection(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.setState(draft => {
      draft.sectionsById[id] = node.mergeIntoValue(value, []);
    });
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  mergeContainerValue(id, value) {
    const node = this.findContainer(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.setState(draft => {
      draft.containersById[id] = node.mergeIntoValue(value, []);
    });
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  mergeModuleValue(id, value) {
    const node = this.findModule(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.setState(draft => {
      draft.modulesById[id] = node.mergeIntoValue(value, []);
    });
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  mergeModuleFieldOverrides(id, fieldOverrides) {
    const node = this.findModule(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.setState(draft => {
      const newFieldOverrides = Object.assign({}, node.getFieldOverrides(), fieldOverrides);
      draft.modulesById[id] = node.mergeIntoValue({
        fieldOverrides: convertMapOfJsonToMapOfAvro(newFieldOverrides)
      }, ['fieldOverrides']);
    });
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  mergeNodeStyles(nodeId, partialStyles, path) {
    const node = this.findNode(nodeId);
    if (isRootNode(node)) {
      throw new Error('Cannot root node has no styles');
    }
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    newTree.setState(draft => {
      if (isSectionNode(node)) {
        draft.sectionsById[node.getId()] = node.mergeIntoStylesByPath(partialStyles, path);
      } else if (isContainerNode(node)) {
        draft.containersById[node.getId()] = node.mergeIntoStylesByPath(partialStyles, path);
      } else if (isModuleNode(node)) {
        draft.modulesById[node.getId()] = node.mergeIntoStylesByPath(partialStyles, path);
      }
    });
    return {
      node: newTree.findNode(nodeId),
      tree: newTree
    };
  }
  mergeSectionStyles(id, breakpointName, partialStyles) {
    var _existingStyles$break;
    const node = this.findSection(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    const existingStyles = node.getStyles();
    const existingBreakpointStyles = (_existingStyles$break = existingStyles.breakpointStyles[breakpointName]) !== null && _existingStyles$break !== void 0 ? _existingStyles$break : {};
    const newBreakpointStyles = Object.assign({}, existingBreakpointStyles, partialStyles);
    const newStyles = Object.assign({}, existingStyles, {
      breakpointStyles: Object.assign({}, existingStyles.breakpointStyles, {
        [breakpointName]: newBreakpointStyles
      })
    });
    newTree.setState(draft => {
      draft.sectionsById[id] = node.mergeIntoStyles(newStyles);
    });
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  mergeContainerStyles(id, breakpointName, partialStyles) {
    var _existingStyles$break2;
    const node = this.findContainer(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    const existingStyles = node.getStyles();
    const existingBreakpointStyles = (_existingStyles$break2 = existingStyles.breakpointStyles[breakpointName]) !== null && _existingStyles$break2 !== void 0 ? _existingStyles$break2 : {};
    const newBreakpointStyles = Object.assign({}, existingBreakpointStyles, partialStyles);
    const newStyles = Object.assign({}, existingStyles, {
      breakpointStyles: Object.assign({}, existingStyles.breakpointStyles, {
        [breakpointName]: newBreakpointStyles
      })
    });
    newTree.setState(draft => {
      draft.containersById[id] = node.mergeIntoStyles(newStyles);
    });
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  mergeModuleStyles(id, options, partialStyles) {
    const node = this.findModule(id);
    const newTree = this.maybeCloneTreeAndSetTreeRefs();
    const existingStyles = node.getStyles();
    if (options.styleType === 'oldModuleStyles') {
      const newStyles = this.createOldModuleStyles(partialStyles, existingStyles);
      newTree.setState(draft => {
        draft.modulesById[id] = node.mergeIntoOldModuleStyles(newStyles);
      });
    } else if (options.styleType === 'editorProvidedModuleBreakpointStyles') {
      const newStyles = this.createdEditorProvidedStyles(partialStyles, existingStyles, options.breakpointName);
      newTree.setState(draft => {
        draft.modulesById[id] = node.mergeIntoEditorProvidedStyles(newStyles);
      });
    } else {
      const newStyles = this.createStyleFieldsStyles(partialStyles, existingStyles, options.breakpointName);
      newTree.setState(draft => {
        draft.modulesById[id] = node.mergeIntoStyleFields(newStyles);
      });
    }
    return {
      node: newTree.findNode(id),
      tree: newTree
    };
  }
  getRootNode() {
    // Explicitly cast to RootNode for now since only GridLayoutDataTree is
    // aware that _state is readonly. Current utils using getRootNode will
    // expect a RootNode but instead get a readonly RootNode.
    return this.getCurrentState().rootNode;
  }
  getRootId() {
    return this.getRootNode().getId();
  }
  hasNode(nodeId, ofType) {
    if (!ofType) {
      return this.hasSection(nodeId) || this.hasContainer(nodeId) || this.hasModule(nodeId);
    }
    if (ofType === GRID_NODE_TYPES.SECTION) {
      return this.hasSection(nodeId);
    } else if (ofType === GRID_NODE_TYPES.CONTAINER) {
      return this.hasContainer(nodeId);
    } else if (ofType === GRID_NODE_TYPES.MODULE) {
      return this.hasModule(nodeId);
    }
    return false;
  }
  findNode(nodeId) {
    return findNodeInTree(this, nodeId);
  }
  findSection(sectionId) {
    if (this.hasSection(sectionId)) {
      return this.getCurrentState().sectionsById[sectionId];
    }
    throw new Error(`No such section ${sectionId} in tree`);
  }
  hasSection(sectionId) {
    return Boolean(this.getCurrentState().sectionsById[sectionId]);
  }

  /**
   * Gets a map of all sections in the tree by id.
   * @returns Map of all sections in the tree by id.
   */
  getAllSectionsById() {
    return this.getCurrentState().sectionsById;
  }

  /**
   * Gets an unordered list of all sections in the tree. For ordered sections, use getAllOrderedSections or getAllOrderedSectionIds.
   * @returns Unordered list of all sections in the tree.
   */
  getAllSections() {
    return Object.values(this.getAllSectionsById());
  }

  /**
   * Gets an nordered list of all section ids in the tree. For ordered sections, use getAllOrderedSections or getAllOrderedSectionIds.
   * @returns Unordered list of all section ids in the tree.
   */
  getSectionIds() {
    return Object.keys(this.getAllSectionsById());
  }

  /**
   * Gets an ordered list of all sections in the tree.
   * @returns Ordered list of all sections in the tree.
   */
  getAllOrderedSections() {
    return this.getRootNode().getChildNodes();
  }

  /**
   * Gets an ordered list of all section ids in the tree.
   * @returns Ordered list of all section ids in the tree.
   */
  getAllOrderedSectionIds() {
    return this.getRootNode().getChildNodeIds();
  }
  findContainer(containerId) {
    if (this.hasContainer(containerId)) {
      return this.getCurrentState().containersById[containerId];
    }
    throw new Error(`No such container ${containerId} in tree`);
  }
  hasContainer(containerId) {
    return Boolean(this.getCurrentState().containersById[containerId]);
  }

  /**
   * Gets a map of all containers in the tree
   * @returns Unordered map of all containers in the tree
   */
  getAllContainersById() {
    return this.getCurrentState().containersById;
  }

  /**
   * Gets an unordered map of all containers in the tree
   * @returns Undordered map of all containers in the tree
   */
  getAllContainers() {
    return Object.values(this.getAllContainersById());
  }

  /**
   * Gets an unordered map of all containers in the tree
   * @returns Unordered map of all containers in the tree
   */
  getContainerIds() {
    return Object.keys(this.getAllContainersById());
  }
  hasModule(moduleId) {
    return Boolean(this.getCurrentState().modulesById[moduleId]);
  }
  findModule(moduleId) {
    if (this.hasModule(moduleId)) {
      return this.getCurrentState().modulesById[moduleId];
    }
    throw new Error(`No such module ${moduleId} in tree`);
  }

  /**
   * Gets a map of all modules in the tree by their id.
   * @returns Map of all modules in the tree by their id
   */
  getAllModulesById() {
    return this.getCurrentState().modulesById;
  }

  /**
   * Gets an unordered list of all modules in the tree
   * @returns Unordered list of all modules in the tree
   */
  getAllModules() {
    return Object.values(this.getAllModulesById());
  }

  /**
   * Gets an unordered list of all module ids in the tree
   * @returns Unordered list of all module ids in the tree
   */
  getModuleIds() {
    return Object.keys(this.getAllModulesById());
  }
  logTreeToConsole() {
    iterateFrom(this.getRootNode(), {
      gridNodeCallback: ({
        gridNode,
        depth
      }) => {
        const whitespace = Array(depth).fill('  ').join('');
        console.log(`${whitespace}${gridNode.getId()} (${gridNode.getType()})`);
      }
    });
  }
  getDestNodeIndex({
    parentNodeId,
    beforeNodeId,
    afterNodeId
  }) {
    if (beforeNodeId && afterNodeId) {
      throw new Error('Cannot specify both beforeNodeId and afterNodeId');
    }
    let destNodeIndex;
    const targetSiblingNodeId = beforeNodeId || afterNodeId;

    // If beforeNodeId is provided, find the index of the sibling node to insert
    // the new node before. If afterNodeId is provided, find the index of the
    // sibling node to insert the new node after. If neither are provided,
    // insert the new node at the end of the parent node.
    if (targetSiblingNodeId) {
      const parentNode = this.findNode(parentNodeId);
      const targetNodeIndex = parentNode.indexOfChildNode(targetSiblingNodeId);

      // For cases where the target sibling node isn't found, then we should
      // fallback to just appending to the end of the parent node.
      if (targetNodeIndex === -1) {
        return targetNodeIndex;
      }
      destNodeIndex = beforeNodeId ? targetNodeIndex : targetNodeIndex + 1;
    } else {
      destNodeIndex = -1;
    }
    return destNodeIndex;
  }
  cloneNodeHelper(nodeToClone, newId) {
    if (isModuleNode(nodeToClone)) {
      return nodeToClone.cloneAndChange({
        id: newId
      })
      // Besides `id`, we'll also want to set the `name` since today we
      // still use the `name` key a lot over `id` for modules
      .mergeIntoValue({
        id: newId,
        name: newId
      }, []);
    } else if (isContainerNode(nodeToClone)) {
      return nodeToClone.cloneAndChange({
        id: newId,
        childNodeIds: []
      }).mergeIntoValue({
        id: newId
      }, []);
    } else if (isSectionNode(nodeToClone)) {
      return nodeToClone.cloneAndChange({
        id: newId,
        childNodeIds: []
      }).mergeIntoValue({
        id: newId
      }, []);
    } else {
      throw new Error(`Unexpected node type encountered while cloning`);
    }
  }
  generateUniqueNodeId(nodeType) {
    if (this.generatedIdQueueReader) {
      const newId = this.generatedIdQueueReader(nodeType);
      if (newId) {
        return newId;
      }
    }
    const newId = generateUniqueId(`${this.getRootId()}-${nodeType}-`);
    if (this.generatedIdQueueWriter) {
      this.generatedIdQueueWriter(nodeType, newId);
    }
    return newId;
  }
  validateNodePlacement(node, parentNode) {
    // Test that nodes follow GridLayout hierarchy rules
    if (isRootNode(node)) {
      throw new Error('Cannot place root node into an existing tree');
    }
    if (isModuleNode(parentNode)) {
      throw new Error('Cannot place node into a module');
    }
    if (isRootNode(parentNode) && !isSectionNode(node)) {
      throw new Error('Can only place sections into root node');
    }
    if (isSectionNode(parentNode)) {
      if (!isContainerNode(node)) {
        throw new Error('Can only place containers into section node');
      }
      if (parentNode.getChildNodeIds().length > 0) {
        throw new Error('Sections can only have one container. Delete the existing container before adding a new one.');
      }
    }
    if (!isContainerNode(parentNode) && isModuleNode(node)) {
      throw new Error('Can only place modules into container node');
    }

    // Test that nodes are not placed into themselves
    if (node.getId() === parentNode.getId()) {
      throw new Error('Cannot place a node into itself');
    }

    // Test that nodes are not placed into their own descendants. This will only
    // be possible if the node is a container or module node
    if (!isSectionNode(node)) {
      iterateFrom(node, {
        includeStartNode: false,
        iterateParents: true,
        gridNodeCallback: ({
          gridNode
        }) => {
          if (gridNode.getId() === node.getId()) {
            throw new Error('Cannot place a node into its own descendant');
          }
        }
      });
    }
    return {
      node,
      parentNode
    };
  }
  isDraftInProgress() {
    return this._isDraftInProgress;
  }
  withBatchUpdate(batchOperation) {
    if (this.isDraftInProgress()) {
      return batchOperation();
    }
    this.startBatchUpdate();
    try {
      const result = batchOperation();
      this.commitBatchUpdate();
      return result;
    } catch (error) {
      this._stateDraft = undefined;
      throw error;
    }
  }
  startBatchUpdate() {
    this._stateDraft = createDraft(this._state);
    this._isDraftInProgress = true;
  }
  commitBatchUpdate() {
    // Not using as a type guard, but instead just as a runtime check to confirm
    // that the draft is in progress. Otherwise, we'd get a type error when
    // trying to set _stateDraft to undefined.
    if (this.isDraftInProgress()) {
      this._state = finishDraft(this._stateDraft);
      this._stateDraft = undefined;
      this._isDraftInProgress = false;
    } else {
      throw new Error('No batch update in progress');
    }
  }
  getCurrentState() {
    return this.isDraftInProgress() ? this._stateDraft : this._state;
  }
  setNodeTreeRefs(tree) {
    tree.setState(draft => {
      draft.rootNode._setTreeRef(tree);
      // Just flatly walking through the nodes in state, which should be a bit
      // faster than using iterateFrom.
      Object.keys(draft.sectionsById).forEach(sectionId => {
        draft.sectionsById[sectionId]._setTreeRef(tree);
      });
      Object.keys(draft.containersById).forEach(containerId => {
        draft.containersById[containerId]._setTreeRef(tree);
      });
      Object.keys(draft.modulesById).forEach(moduleId => {
        draft.modulesById[moduleId]._setTreeRef(tree);
      });
    });
  }
  setState(stateSetter) {
    if (this.isDraftInProgress()) {
      stateSetter(this._stateDraft);
    } else {
      this._state = produce(this._state, stateSetter);
    }
  }

  /**
   * If no draft is in progress, returns the current tree. Otherwise, creates a
   * new tree with the same structure as the current tree but with the tree refs
   * on each node set to the new tree.
   *
   * @returns A new tree with the same structure as the current tree but with
   * the tree refs on each node set to the new tree.
   */
  maybeCloneTreeAndSetTreeRefs() {
    if (this.isDraftInProgress()) {
      return this;
    }
    const newTree = new BaseGridLayoutDataTree({
      RootNodeClass: this.RootNodeClass,
      SectionNodeClass: this.SectionNodeClass,
      ContainerNodeClass: this.ContainerNodeClass,
      ModuleNodeClass: this.ModuleNodeClass,
      rootId: this.getRootId(),
      rootNodeValue: this.getRootNode().getValue(),
      initialState: this._state,
      generatedIdQueueWriter: this.generatedIdQueueWriter,
      generatedIdQueueReader: this.generatedIdQueueReader
    });
    newTree.setNodeTreeRefs(newTree);
    return newTree;
  }
  createOldModuleStyles(partialStyles, existingStyles) {
    const newStyles = {};
    if ('css' in partialStyles) {
      var _existingStyles$oldMo, _existingStyles$oldMo2;
      newStyles.css = Object.assign({}, convertMapOfJsonToMapOfAvro((_existingStyles$oldMo = (_existingStyles$oldMo2 = existingStyles.oldModuleStyles) === null || _existingStyles$oldMo2 === void 0 ? void 0 : _existingStyles$oldMo2.css) !== null && _existingStyles$oldMo !== void 0 ? _existingStyles$oldMo : {}), partialStyles.css);
    }
    if ('childCss' in partialStyles) {
      var _existingStyles$oldMo3, _existingStyles$oldMo4;
      newStyles.childCss = Object.assign({}, convertMapOfJsonToMapOfAvro((_existingStyles$oldMo3 = (_existingStyles$oldMo4 = existingStyles.oldModuleStyles) === null || _existingStyles$oldMo4 === void 0 ? void 0 : _existingStyles$oldMo4.childCss) !== null && _existingStyles$oldMo3 !== void 0 ? _existingStyles$oldMo3 : {}), partialStyles.childCss);
    }
    return newStyles;
  }
  createdEditorProvidedStyles(partialStyles, existingStyles, breakpointName) {
    var _existingStyles$edito, _existingStyles$edito2, _existingStyles$edito3;
    const existingBreakpointStyles = (_existingStyles$edito = (_existingStyles$edito2 = existingStyles.editorProvidedModuleBreakpointStyles) === null || _existingStyles$edito2 === void 0 ? void 0 : _existingStyles$edito2.breakpointStyles[breakpointName]) !== null && _existingStyles$edito !== void 0 ? _existingStyles$edito : {};
    const newBreakpointStyles = Object.assign({}, existingBreakpointStyles, partialStyles);
    const newStyles = Object.assign({}, (_existingStyles$edito3 = existingStyles.editorProvidedModuleBreakpointStyles) === null || _existingStyles$edito3 === void 0 ? void 0 : _existingStyles$edito3.breakpointStyles, {
      [breakpointName]: newBreakpointStyles
    });
    return newStyles;
  }
  createStyleFieldsStyles(partialStyles, existingStyles, breakpointName) {
    var _existingStyles$style, _existingStyles$style2, _existingStyles$style3;
    const existingBreakpointStyles = (_existingStyles$style = (_existingStyles$style2 = existingStyles.styleFieldsModuleBreakpointStyles) === null || _existingStyles$style2 === void 0 || (_existingStyles$style2 = _existingStyles$style2.breakpointStyles[breakpointName]) === null || _existingStyles$style2 === void 0 ? void 0 : _existingStyles$style2.styleFields) !== null && _existingStyles$style !== void 0 ? _existingStyles$style : {};
    const newBreakpointStyles = {};
    if ('styleFields' in partialStyles) {
      newBreakpointStyles.styleFields = Object.assign({}, convertMapOfJsonToMapOfAvro(existingBreakpointStyles), partialStyles.styleFields);
    }
    const newStyles = Object.assign({}, (_existingStyles$style3 = existingStyles.styleFieldsModuleBreakpointStyles) === null || _existingStyles$style3 === void 0 ? void 0 : _existingStyles$style3.breakpointStyles, newBreakpointStyles);
    return newStyles;
  }
}